/**
 * HelpPage - Auto-discovered node reference overlay
 *
 * Reads all registered NodeDefs from the plugin registry and displays them
 * grouped by domain -> namespace -> category, with endpoint tables.
 * Search/filter at top. Only shows param/internal endpoints (not stream).
 */

import React, { useState, useMemo, useCallback, useRef, useEffect, type CSSProperties } from 'react';
import { webRuntimeApi } from '@/runtime/web';
import type { NodeDef } from '@/runtime/IRuntime';
import type { Endpoint } from '@/nodes/Endpoint';

interface HelpPageProps {
  open: boolean;
  onClose: () => void;
}

// Parse node type to extract namespace and base type
function parseNodeType(type: string): { namespace: string | null; baseType: string } {
  const dotIndex = type.indexOf('.');
  if (dotIndex !== -1) {
    return { namespace: type.substring(0, dotIndex), baseType: type.substring(dotIndex + 1) };
  }
  return { namespace: null, baseType: type };
}

const STREAM_TYPES = new Set(['audio', 'video', 'hydra', 'nodebox', 'three', 'two']);

function isUserFacingEndpoint(ep: Endpoint): boolean {
  const dt = ep.dataType ?? 'number';
  // Skip stream endpoints — they aren't user-facing params
  if (STREAM_TYPES.has(dt)) return false;
  const flow = ep.dataFlow ?? 'param';
  return flow === 'param' || flow === 'event' || flow === 'trigger' || flow === 'internal';
}

interface NodeEntry {
  def: NodeDef;
  namespace: string | null;
  baseType: string;
}

export function HelpPage({ open, onClose }: HelpPageProps) {
  const [search, setSearch] = useState('');
  const [activeDomain, setActiveDomain] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Focus search on open
  useEffect(() => {
    if (open && searchRef.current) {
      searchRef.current.focus();
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Get all node defs from plugin registry
  const allDefs = useMemo(() => {
    if (!open) return [];
    return webRuntimeApi.getPluginRegistry().getAllNodeDefs();
  }, [open]);

  // Group by domain -> namespace -> category
  const grouped = useMemo(() => {
    const result: Record<string, Record<string, Record<string, NodeEntry[]>>> = {};

    for (const def of allDefs) {
      const { namespace, baseType } = parseNodeType(def.type);
      const domain = def.domain ?? 'data';
      const ns = namespace ?? '_root';
      const cat = def.category ?? '_uncategorized';

      if (!result[domain]) result[domain] = {};
      if (!result[domain][ns]) result[domain][ns] = {};
      if (!result[domain][ns][cat]) result[domain][ns][cat] = [];

      result[domain][ns][cat].push({ def, namespace, baseType });
    }

    return result;
  }, [allDefs]);

  // Filter by search
  const matchesSearch = useCallback((entry: NodeEntry): boolean => {
    if (!search) return true;
    const term = search.toLowerCase();
    const { def, baseType } = entry;
    if (def.type.toLowerCase().includes(term)) return true;
    if (baseType.toLowerCase().includes(term)) return true;
    if (def.label?.toLowerCase().includes(term)) return true;
    if (def.shortName?.toLowerCase().includes(term)) return true;
    if (def.info?.toLowerCase().includes(term)) return true;
    if (def.category?.toLowerCase().includes(term)) return true;
    if (def.keywords?.some(kw => kw.toLowerCase().includes(term))) return true;
    return false;
  }, [search]);

  if (!open) return null;

  const domainOrder = ['data', 'audio', 'video'];
  const domainLabels: Record<string, string> = { data: 'Data', audio: 'Audio', video: 'Video' };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.container} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.title}>Node Reference</h2>
          <input
            ref={searchRef}
            type="text"
            placeholder="Search nodes..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={styles.searchInput}
          />
          <button onClick={onClose} style={styles.closeButton}>
            &times;
          </button>
        </div>

        {/* Domain filter tabs */}
        <div style={styles.domainTabs}>
          {domainOrder.map(domain => {
            if (!grouped[domain]) return null;
            const isActive = activeDomain === domain;
            return (
              <button
                key={domain}
                onClick={() => setActiveDomain(isActive ? null : domain)}
                style={{
                  ...styles.domainTab,
                  background: isActive ? '#4b5563' : 'transparent',
                  color: isActive ? '#e5e7eb' : '#9ca3af',
                }}
              >
                {domainLabels[domain] ?? domain}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div style={styles.content}>
          {domainOrder.map(domain => {
            if (activeDomain && domain !== activeDomain) return null;
            const domainData = grouped[domain];
            if (!domainData) return null;

            // Check if anything matches in this domain
            const domainEntries: NodeEntry[] = [];
            for (const nsData of Object.values(domainData)) {
              for (const catData of Object.values(nsData)) {
                domainEntries.push(...catData);
              }
            }
            const filtered = domainEntries.filter(matchesSearch);
            if (filtered.length === 0) return null;

            return (
              <div key={domain}>
                <h3 style={styles.domainHeader}>{domainLabels[domain] ?? domain}</h3>

                {Object.entries(domainData).map(([ns, nsData]) => {
                  // Filter entries in this namespace
                  const nsEntries: NodeEntry[] = [];
                  for (const catData of Object.values(nsData)) {
                    nsEntries.push(...catData);
                  }
                  const nsFiltered = nsEntries.filter(matchesSearch);
                  if (nsFiltered.length === 0) return null;

                  return (
                    <div key={ns}>
                      {ns !== '_root' && (
                        <h4 style={styles.nsHeader}>
                          {ns.charAt(0).toUpperCase() + ns.slice(1)} Plugin
                        </h4>
                      )}

                      {Object.entries(nsData).map(([cat, entries]) => {
                        const catFiltered = entries.filter(matchesSearch);
                        if (catFiltered.length === 0) return null;

                        return (
                          <div key={cat}>
                            {cat !== '_uncategorized' && (
                              <h5 style={styles.catHeader}>
                                {cat.charAt(0).toUpperCase() + cat.slice(1)}
                              </h5>
                            )}

                            {catFiltered.map(entry => (
                              <NodeCard key={entry.def.type} entry={entry} />
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            );
          })}

          {/* Also show domains not in domainOrder */}
          {Object.keys(grouped)
            .filter(d => !domainOrder.includes(d))
            .map(domain => {
              const domainData = grouped[domain];
              const entries: NodeEntry[] = [];
              for (const nsData of Object.values(domainData)) {
                for (const catData of Object.values(nsData)) {
                  entries.push(...catData);
                }
              }
              const filtered = entries.filter(matchesSearch);
              if (filtered.length === 0) return null;

              return (
                <div key={domain}>
                  <h3 style={styles.domainHeader}>{domain}</h3>
                  {filtered.map(entry => (
                    <NodeCard key={entry.def.type} entry={entry} />
                  ))}
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}

function EndpointRow({ id, ep }: { id: string; ep: Endpoint }) {
  return (
    <tr>
      <td style={styles.td}>
        <span style={styles.epName}>{ep.label ?? id}</span>
        {ep.label && ep.label !== id && (
          <span style={styles.epId}>{id}</span>
        )}
      </td>
      <td style={styles.td}>{ep.dataType ?? 'number'}</td>
      <td style={styles.td}>{ep.dataFlow ?? 'param'}</td>
      <td style={{ ...styles.td, ...styles.epInfo }}>{ep.info ?? ''}</td>
    </tr>
  );
}

function NodeCard({ entry }: { entry: NodeEntry }) {
  const [expanded, setExpanded] = useState(false);
  const { def, baseType } = entry;
  const displayName = def.label ?? baseType;

  // Filter to user-facing endpoints, then split by group
  const { groups, ungrouped } = useMemo(() => {
    const groupMap = new Map<string, [string, Endpoint][]>();
    const noGroup: [string, Endpoint][] = [];

    for (const [id, ep] of Object.entries(def.endpoints)) {
      if (!isUserFacingEndpoint(ep)) continue;
      const group = ep.group;
      if (group) {
        if (!groupMap.has(group)) groupMap.set(group, []);
        groupMap.get(group)!.push([id, ep]);
      } else {
        noGroup.push([id, ep]);
      }
    }

    return { groups: groupMap, ungrouped: noGroup };
  }, [def.endpoints]);

  const totalEndpoints = ungrouped.length + [...groups.values()].reduce((n, g) => n + g.length, 0);

  return (
    <div style={styles.card}>
      <div
        style={styles.cardHeader}
        onClick={() => totalEndpoints > 0 && setExpanded(e => !e)}
      >
        {totalEndpoints > 0 && (
          <span style={styles.cardChevron}>{expanded ? '▼' : '▶'}</span>
        )}
        <span style={styles.cardName}>{displayName}</span>
        {def.shortName && def.shortName !== displayName && (
          <span style={styles.cardShortName}>{def.shortName}</span>
        )}
        <span style={styles.cardType}>{def.type}</span>
      </div>

      {def.info && <p style={styles.cardInfo}>{def.info}</p>}

      {expanded && totalEndpoints > 0 && (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Endpoint</th>
              <th style={styles.th}>Type</th>
              <th style={styles.th}>Flow</th>
              <th style={styles.th}>Info</th>
            </tr>
          </thead>
          <tbody>
            {ungrouped.map(([id, ep]) => (
              <EndpointRow key={id} id={id} ep={ep} />
            ))}

            {[...groups.entries()].map(([groupName, groupEps]) => (
              <React.Fragment key={groupName}>
                <tr>
                  <td colSpan={4} style={styles.groupRow}>
                    {groupName}
                  </td>
                </tr>
                {groupEps.map(([id, ep]) => (
                  <EndpointRow key={id} id={id} ep={ep} />
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.75)',
    zIndex: 10000,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingTop: 40,
    overflow: 'auto',
  },
  container: {
    width: '90%',
    maxWidth: 900,
    maxHeight: 'calc(100vh - 80px)',
    background: '#111827',
    borderRadius: 8,
    border: '1px solid #374151',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '16px 20px',
    borderBottom: '1px solid #374151',
    flexShrink: 0,
  },
  title: {
    margin: 0,
    fontSize: 18,
    fontWeight: 600,
    color: '#e5e7eb',
    whiteSpace: 'nowrap',
  },
  searchInput: {
    flex: 1,
    background: '#1f2937',
    border: '1px solid #4b5563',
    borderRadius: 4,
    padding: '8px 12px',
    color: 'white',
    fontSize: 13,
    outline: 'none',
  },
  closeButton: {
    background: 'transparent',
    border: 'none',
    color: '#9ca3af',
    fontSize: 24,
    cursor: 'pointer',
    padding: '0 4px',
    lineHeight: 1,
  },
  domainTabs: {
    display: 'flex',
    gap: 4,
    padding: '8px 20px',
    borderBottom: '1px solid #374151',
    flexShrink: 0,
  },
  domainTab: {
    padding: '6px 14px',
    border: 'none',
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: '16px 20px',
  },
  domainHeader: {
    margin: '24px 0 12px',
    fontSize: 16,
    fontWeight: 600,
    color: '#60a5fa',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #1f2937',
    paddingBottom: 4,
  },
  nsHeader: {
    margin: '16px 0 8px',
    fontSize: 13,
    fontWeight: 500,
    color: '#d1d5db',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  catHeader: {
    margin: '12px 0 6px',
    fontSize: 12,
    fontWeight: 500,
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  card: {
    marginBottom: 12,
    padding: '10px 14px',
    background: '#1f2937',
    borderRadius: 6,
    border: '1px solid #374151',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
    marginBottom: 4,
    cursor: 'pointer',
    userSelect: 'none',
  },
  cardChevron: {
    fontSize: 8,
    color: '#6b7280',
    width: 10,
    flexShrink: 0,
  },
  cardName: {
    fontWeight: 600,
    fontSize: 14,
    color: '#e5e7eb',
  },
  cardShortName: {
    fontSize: 12,
    color: '#6b7280',
  },
  cardType: {
    marginLeft: 'auto',
    fontSize: 11,
    color: '#4b5563',
    fontFamily: 'monospace',
  },
  cardInfo: {
    margin: '4px 0 8px',
    fontSize: 12,
    color: '#9ca3af',
    lineHeight: '1.4',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 11,
  },
  th: {
    textAlign: 'left',
    padding: '4px 8px',
    color: '#6b7280',
    fontWeight: 500,
    borderBottom: '1px solid #374151',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '3px 8px',
    color: '#d1d5db',
    borderBottom: '1px solid #1f2937',
    verticalAlign: 'top',
  },
  epName: {
    fontWeight: 500,
  },
  epId: {
    marginLeft: 6,
    fontSize: 10,
    color: '#4b5563',
    fontFamily: 'monospace',
  },
  epInfo: {
    color: '#9ca3af',
    maxWidth: 200,
  },
  groupRow: {
    padding: '6px 8px 3px',
    fontSize: 10,
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #374151',
  },
};
